import { Controller } from "@hotwired/stimulus"

// Keeps the keyboard focus when a Turbo Stream replaces #shop_cart: the focused control is destroyed
// with the old block, so its data-focus-key is remembered and the twin in the new DOM takes the focus.
// When the twin is gone (line removed), the block itself gets it. No optional chaining (Webpacker 5).
// Wired on an element OUTSIDE #shop_cart; the stream element lives in <body>, hence @document.
//
// Turbo disables the submit button while the request is in flight, which drops the focus to <body>
// before the stream arrives. The key is therefore also taken when the form is submitted (capture,
// before Turbo disables anything) and used when nothing inside the block holds the focus anymore.
export default class extends Controller {
  connect() {
    this.submittedKey = null
  }

  remember() {
    this.submittedKey = this.focusKeyWithinCart()
  }

  wrap(event) {
    const stream = event.target
    if (!stream || stream.getAttribute("target") !== "shop_cart") return

    const current = this.focusKeyWithinCart()
    // The submitted key only applies when Turbo's disabled submitter dropped the focus to <body>;
    // a user who moved focus elsewhere meanwhile keeps it.
    const key = current !== null ? current : (document.activeElement === document.body ? this.submittedKey : null)
    this.submittedKey = null
    const originalRender = event.detail.render

    event.detail.render = async (streamElement) => {
      await originalRender(streamElement)
      this.restore(key)
    }
  }

  focusKeyWithinCart() {
    const cart = document.getElementById("shop_cart")
    const active = document.activeElement
    if (!cart || !active || !cart.contains(active)) return null

    const keyed = active.closest("[data-focus-key]")
    return keyed ? keyed.dataset.focusKey : null
  }

  restore(key) {
    const cart = document.getElementById("shop_cart")
    if (!cart || key === null) return

    const twin = Array.from(cart.querySelectorAll("[data-focus-key]")).find((el) => el.dataset.focusKey === key)
    if (twin && !twin.disabled) {
      twin.focus()
      return
    }

    // A « + » that just reached the stock is disabled: hand the focus to the same line's « − ».
    const match = /^(line-.+)-inc$/.exec(key)
    const dec = match ? Array.from(cart.querySelectorAll("[data-focus-key]")).find((el) => el.dataset.focusKey === match[1] + "-dec") : null
    if (twin && dec && !dec.disabled) {
      dec.focus()
    } else {
      cart.focus({ preventScroll: true })
    }
  }
}
